import moment from "moment";

// Interfaces
import { IPayPeriod } from "@/Common/api/types/CommissionCycle";
import { IUser } from "@/Common/interfaces/user";
import { IChat } from "@/Washouts/types/PayrollClosing/queries";
import { IAccessPositions, ISelected, IWashoutData } from "@/Washouts/types/WashoutReview";

// Helpers
import { getDepartmentalPosition } from "@/Common/helpers/Positions";
import { isEveryProviderCompleted, StatusKey } from "../Statuses";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";
import { DATE_FORMAT } from "@/Constants/date";
import { Nullable } from "@/Common/interfaces/types";

export const getCurrentPayPeriod = (payPeriods: IPayPeriod[] = [], payPeriodId: string | null = null) => {
	if (payPeriodId) {
		return payPeriods?.find((period) => period?._id === payPeriodId);
	}
	return payPeriods?.find((period) => period?.inReview && !isEveryProviderCompleted(period));
};

export const getAccessPosition = (requiredAccess: IAccessPositions, position: string) => {
	if (!requiredAccess || !position) {
		return { access: false, edit: false };
	}
	const departmentalPosition = getDepartmentalPosition(position);
	return requiredAccess?.[departmentalPosition] || { access: false, edit: false };
};

export const resolveCheck = (washoutData?: IWashoutData, currentPayPeriod?: IPayPeriod) => {
	const isApproved = washoutData?.review?.status === "APPROVED";
	const isEditableWashout = !isEveryProviderCompleted(currentPayPeriod);
	return { isEditableWashout, isApproved };
};

export const checkEditablePosition = (data?: IWashoutData, access?: IAccessPositions) => {
	if (!data?.employee?.position || !access) {
		return false;
	}
	const departmentalPosition = getDepartmentalPosition(data?.employee?.position);
	return access?.[departmentalPosition]?.edit || false;
};

export const resolveLoading = (...params: boolean[]) => params.some((item: boolean) => item);

export const resolveImage = (selected: ISelected | null) => (selected ? emptyTable : empty);

export const resolveEmptyText = (selected: ISelected | null) =>
	selected ? "No washout found for this employee" : "Select an employee to view their washout";

export const checkIsSameUser = (userMessageId: string, myUserId?: string) => userMessageId === myUserId;

export const isSameMinute = (date1: Date, date2: Date): boolean => {
	return moment(date1).isSame(date2, "minute");
};

export const getMessageBorderRadius = (
	isCurrentUser: boolean,
	isPreviousMessageSameUser = false,
	isNextMessageSameUser = false,
	isPreviousMessageSameMinute = false,
	isNextMessageSameMinute = false
): string => {
	const sameUser = isPreviousMessageSameUser && isNextMessageSameUser;
	const sameMinute = isPreviousMessageSameMinute && isNextMessageSameMinute;

	if (sameUser && sameMinute) {
		return isCurrentUser ? "10px 0px 0px 10px" : "0px 10px 10px 0px";
	}

	if (isPreviousMessageSameUser && isPreviousMessageSameMinute) {
		return isCurrentUser ? "10px 0px 10px 10px" : "0px 10px 10px 10px";
	}

	if (isNextMessageSameUser && isNextMessageSameMinute) {
		return isCurrentUser ? "10px 10px 0px 10px" : "10px 10px 10px 0px";
	}

	return "10px 10px 10px 10px"; // Default border radius
};

export const resolveWashoutButtonStyles = ({
	openPopper,
	alertMessage,
	attachedFilesLength,
}: {
	// isVisible: boolean;
	openPopper: boolean;
	alertMessage: string;
	attachedFilesLength: number;
}) => {
	const POPPER_BOTTOM = 140;
	const ALERT_MESSAGE_BOTTOM = 125;
	const DEFAULT_BOTTOM = 75;
	const forty = 40;
	const seventyFive = 75;
	const oneTen = 110;
	const three = 3;
	const four = 4;
	const five = 5;
	const six = 6;

	let bottom;
	if (openPopper) {
		bottom = POPPER_BOTTOM;
	} else if (alertMessage) {
		bottom = ALERT_MESSAGE_BOTTOM;
	} else {
		bottom = DEFAULT_BOTTOM;
	}

	if (attachedFilesLength === 1 || attachedFilesLength === 2) {
		bottom += forty;
	} else if (attachedFilesLength === three || attachedFilesLength === four) {
		bottom += seventyFive;
	} else if (attachedFilesLength === five || attachedFilesLength === six) {
		bottom += oneTen;
	}

	return {
		"&:hover": { color: "var(--primary-color)" },
		backgroundColor: "white",
		fontSize: "35px",
		boxShadow: "0px 2px 8px rgba(0,0,0,0.32)",
		zIndex: 9999,
		position: "absolute",
		right: 10,
		transition: "bottom 0.3s",
		bottom,
	};
};

export const resolveChatRequiredParameters = ({
	data,
	msg,
	user,
	index,
}: {
	data?: IChat[];
	msg: IChat;
	user: IUser | null;
	index: number;
}) => {
	const isCurrentUser = checkIsSameUser(msg.createdBy._id, user?._id);
	const previousMessage = data?.[index - 1];
	const nextMessage = data?.[index + 1];
	const isPreviousMessageSameUser = previousMessage && checkIsSameUser(previousMessage.createdBy._id, msg.createdBy._id);
	const isNextMessageSameUser = nextMessage && checkIsSameUser(nextMessage.createdBy._id, msg.createdBy._id);
	const isPreviousMessageSameMinute = previousMessage && isSameMinute(new Date(msg.createdOn), new Date(previousMessage.createdOn));
	const isNextMessageSameMinute = nextMessage && isSameMinute(new Date(msg.createdOn), new Date(nextMessage.createdOn));
	const isNextMessageFromSameUserAndTime = isNextMessageSameUser && isNextMessageSameMinute;
	const borderRadius = getMessageBorderRadius(
		isCurrentUser,
		isPreviousMessageSameUser,
		isNextMessageSameUser,
		isPreviousMessageSameMinute,
		isNextMessageSameMinute
	);
	const isFirstMessageFromCurrentUser = isCurrentUser && !isPreviousMessageSameUser;
	const isFirstMessageFromNotCurrentUser = !isCurrentUser && !isPreviousMessageSameUser;
	const formattedDate = moment(msg.createdOn).format(DATE_FORMAT.shortDateUnpaddedTime12h);
	return {
		isCurrentUser,
		previousMessage,
		nextMessage,
		isPreviousMessageSameUser,
		isNextMessageSameUser,
		isPreviousMessageSameMinute,
		isNextMessageSameMinute,
		isNextMessageFromSameUserAndTime,
		borderRadius,
		isFirstMessageFromCurrentUser,
		isFirstMessageFromNotCurrentUser,
		formattedDate,
	};
};

export const getPayPeriodPositionDisableByAccess = (payPeriod: Nullable<IPayPeriod>, accessPositions: IAccessPositions) => {
	if (!payPeriod?.positionStatus?.length) {
		return [];
	}

	return payPeriod.positionStatus
		? payPeriod.positionStatus
				?.filter((item) => {
					const positionAccess = getAccessPosition(accessPositions, item.position);
					return !positionAccess?.access;
				})
				.map((item) => item.position)
		: [];
};

export const hasPayPeriodPositionInStatus = ({
	payPeriod,
	status,
	washoutConditionsByPosition,
	statusKey,
}: {
	payPeriod: Nullable<IPayPeriod>;
	status: string[];
	washoutConditionsByPosition: Record<string, StatusKey[]>;
	statusKey: StatusKey;
}) => {
	if (!payPeriod?.positionStatus?.length) {
		return false;
	}

	return payPeriod.positionStatus.some(
		(item) => status.includes(item.status) && washoutConditionsByPosition?.[item.position]?.includes(statusKey)
	);
};
